import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";

const titleMap = {
  TC: "TRANSFER CERTIFICATE",
  BC: "BONAFIDE CERTIFICATE",
  CC: "CHARACTER CERTIFICATE",
  FC: "FEE CERTIFICATE",
};

const getValue = (certificate, keys) => {
  for (const key of keys) {
    const value = certificate?.[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== "") {
      return value;
    }
  }

  return "";
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB").replace(/\//g, "-");
};

const buildRows = (documentType, certificate) => {
  const classValue = [
    getValue(certificate, ["classname", "class_last_studied", "classId"]),
    getValue(certificate, ["sectionname", "sectionId"]),
  ]
    .filter(Boolean)
    .join(" ");

  const rowsByType = {
    TC: [
      ["Name of the student", getValue(certificate, ["studentname"])],
      ["Name of the father/Husband", getValue(certificate, ["father_name", "fathername"])],
      ["Name of the Mother", getValue(certificate, ["mother_name", "mothername"])],
      ["Nationality", getValue(certificate, ["nationality"])],
      ["Religion and Caste", getValue(certificate, ["category"])],
      ["Date of Birth", formatDate(getValue(certificate, ["dob"]))],
      ["Permanent Address", getValue(certificate, ["resident_address", "residentAddress"])],
      ["Registration Number", getValue(certificate, ["registration_no", "school_admission_no", "admission_no"])],
      ["Date of Admission", formatDate(getValue(certificate, ["date_of_admission"]))],
      ["Class in which the student was studying at the time of leaving the college", classValue],
      ["Whether qualified for promotion to higher class", getValue(certificate, ["qualified_for_promotion"])],
      ["Reason for the issue of transfer certificate", getValue(certificate, ["reason_for_tc"])],
      ["Conduct and character during the period of study", getValue(certificate, ["general_conduct"])],
      ["Date of leaving from college", formatDate(getValue(certificate, ["tc_issued_date", "cancelled_on"]))],
    ],
    BC: [
      ["Name of the student", getValue(certificate, ["studentname"])],
      ["Name of the father/Husband", getValue(certificate, ["father_name", "fathername"])],
      ["Name of the Mother", getValue(certificate, ["mother_name", "mothername"])],
      ["Class", classValue],
      ["Application Date", formatDate(getValue(certificate, ["tc_applied_date"]))],
      ["Issue Date", formatDate(getValue(certificate, ["tc_issued_date"]))],
      ["Remarks", getValue(certificate, ["other_remarks"])],
    ],
    CC: [
      ["Name of the student", getValue(certificate, ["studentname"])],
      ["Name of the father/Husband", getValue(certificate, ["father_name", "fathername"])],
      ["Name of the Mother", getValue(certificate, ["mother_name", "mothername"])],
      ["Date of Birth", formatDate(getValue(certificate, ["dob"]))],
      ["Class", classValue],
      ["Registration Number", getValue(certificate, ["cbse_reg_no", "registration_no"])],
      ["Board Roll Number", getValue(certificate, ["rollno"])],
      ["Marks Obtained", getValue(certificate, ["marks_obtained"])],
      ["Subjects Studied", getValue(certificate, ["subjects_studied"])],
      ["General Conduct", getValue(certificate, ["general_conduct"])],
      ["NCC Cadet Details", getValue(certificate, ["ncc_cadet_details"])],
      ["Cultural Activities", getValue(certificate, ["cultural_activities"])],
      ["Games Played Details", getValue(certificate, ["games_played_details"])],
      ["Other Activities", getValue(certificate, ["other_activities"])],
      ["Application Date", formatDate(getValue(certificate, ["tc_applied_date"]))],
      ["Issue Date", formatDate(getValue(certificate, ["tc_issued_date"]))],
      ["Remarks", getValue(certificate, ["other_remarks"])],
    ],
    FC: [
      ["Name of the student", getValue(certificate, ["studentname"])],
      ["Name of the father/Husband", getValue(certificate, ["father_name", "fathername"])],
      ["Name of the Mother", getValue(certificate, ["mother_name", "mothername"])],
      ["Class", classValue],
      ["From Month", getValue(certificate, ["from_month"])],
      ["To Month", getValue(certificate, ["to_month"])],
      ["Issue Date", formatDate(getValue(certificate, ["tc_issued_date", "tc_applied_date"]))],
      ["Remarks", getValue(certificate, ["other_remarks"])],
    ],
  };

  return rowsByType[documentType] || [];
};

const CertificatePreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialCertificate = location.state?.certificate || location.state || null;
  const [certificate, setCertificate] = useState(initialCertificate);
  const [loading, setLoading] = useState(!initialCertificate);
  const [error, setError] = useState("");

  const documentType =
    location.state?.documentType ||
    getValue(initialCertificate || {}, ["documentType", "document_type"]) ||
    localStorage.getItem("documentType") ||
    localStorage.getItem("document_type") ||
    "TC";

  useEffect(() => {
    if (initialCertificate) {
      return;
    }

    const academicYearId = localStorage.getItem("academicSessionId");
    const orgId = localStorage.getItem("orgId");
    const branchId = localStorage.getItem("branchId");
    const studentId = localStorage.getItem("studentId");
    const transferCertificateId = localStorage.getItem("transfer_certificate_id");

    if (!academicYearId || !orgId || !branchId || !studentId) {
      setError("Certificate data is not available.");
      setLoading(false);
      return;
    }

    const apiUrl = `${ApiUrl.apiurl}STUDENT_CERTIFICATE/GetStudentDetailsBasedOnDocumentTypeStudentId/?academic_year_id=${academicYearId}&orgId=${orgId}&branchId=${branchId}&document_type=${documentType}&studentId=${studentId}&transfer_certificate_id=${transferCertificateId || ""}`;

    fetch(apiUrl)
      .then((response) => response.json())
      .then((result) => {
        if (result.message === "success" && result.data) {
          const data = Array.isArray(result.data) ? result.data[0] : result.data;
          setCertificate(data);
          setError("");
        } else {
          setError(result.message || "Failed to load certificate.");
        }
      })
      .catch(() => {
        setError("Failed to load certificate.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [documentType, initialCertificate]);

  const mergedCertificate = useMemo(() => {
    if (!certificate) {
      return null;
    }

    return {
      ...certificate,
      ...(certificate.studentcertificatedetails || {}),
    };
  }, [certificate]);

  const documentNo = mergedCertificate
    ? getValue(mergedCertificate, ["document_no"]) ||
      [
        getValue(mergedCertificate, ["transfer_certificate_no_prefix"]),
        getValue(mergedCertificate, ["transfer_certificate_no_postfix"]),
      ]
        .filter(Boolean)
        .join("/")
    : "";

  const issuedDate = mergedCertificate
    ? formatDate(
        getValue(mergedCertificate, ["tc_issued_date", "tc_applied_date", "date_of_admission"])
      )
    : "";

  const studentName = mergedCertificate
    ? getValue(mergedCertificate, ["studentname"])
    : "";

  const rows = mergedCertificate ? buildRows(documentType, mergedCertificate) : [];

  return (
    <div className="container-fluid" style={{ background: "#dfe9f8", minHeight: "100vh", padding: "24px 12px" }}>
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="d-flex gap-2 mb-3">
            <button type="button" className="btn btn-danger" onClick={() => navigate("/admin/student-certificate")}>Close</button>
          </div>

          {loading ? <div className="text-center">Loading certificate...</div> : null}
          {error ? <div className="alert alert-danger">{error}</div> : null}

          {!loading && !error && mergedCertificate ? (
            <div
              style={{
                maxWidth: "820px",
                margin: "0 auto",
                background: "#fff",
                border: "1px solid #1d1d1d",
                padding: "24px 34px 30px",
                position: "relative",
                boxShadow: "0 12px 30px rgba(0, 0, 0, 0.12)",
              }}
            >
              <div style={{ position: "absolute", inset: "10px", border: "1px solid #1d1d1d", pointerEvents: "none" }} />
              <div style={{ position: "absolute", top: "18px", left: "18px", width: "22px", height: "22px", borderTop: "3px solid #1d1d1d", borderLeft: "3px solid #1d1d1d" }} />
              <div style={{ position: "absolute", top: "18px", right: "18px", width: "22px", height: "22px", borderTop: "3px solid #1d1d1d", borderRight: "3px solid #1d1d1d" }} />
              <div style={{ position: "absolute", bottom: "18px", left: "18px", width: "22px", height: "22px", borderBottom: "3px solid #1d1d1d", borderLeft: "3px solid #1d1d1d" }} />
              <div style={{ position: "absolute", bottom: "18px", right: "18px", width: "22px", height: "22px", borderBottom: "3px solid #1d1d1d", borderRight: "3px solid #1d1d1d" }} />

              <div style={{ position: "relative", zIndex: 1 }}>
                <div className="d-flex justify-content-between align-items-center mb-3" style={{ fontSize: "14px", fontWeight: 600 }}>
                  <div>
                    Ref No - <span style={{ display: "inline-block", minWidth: "140px", borderBottom: "1px solid #1d1d1d", paddingBottom: "2px" }}>{documentNo || "-"}</span>
                  </div>
                  <div>
                    Date - <span style={{ display: "inline-block", minWidth: "120px", borderBottom: "1px solid #1d1d1d", paddingBottom: "2px" }}>{issuedDate || "-"}</span>
                  </div>
                </div>

                <h3 className="text-center" style={{ fontSize: "28px", fontWeight: 700, letterSpacing: "1px", margin: "24px 0 18px" }}>
                  {titleMap[documentType] || "CERTIFICATE"}
                </h3>

                <p className="text-center" style={{ marginBottom: "28px", fontSize: "15px" }}>
                  This is to certify that <span style={{ display: "inline-block", minWidth: "180px", borderBottom: "1px solid #1d1d1d", textAlign: "center" }}>{studentName || "-"}</span> was a bonafide student of this institution.
                </p>

                <div>
                  {rows.map(([label, value], index) => (
                    <div key={`${label}-${index}`} className="row align-items-start" style={{ marginBottom: "10px", fontSize: "14px" }}>
                      <div className="col-12 col-md-5 d-flex">
                        <span style={{ width: "18px" }}>•</span>
                        <span>{label}</span>
                      </div>
                      <div className="col-1 d-none d-md-block">:</div>
                      <div className="col-12 col-md-6">
                        <span style={{ display: "inline-block", width: "100%", minHeight: "24px", borderBottom: "1px solid #1d1d1d", paddingBottom: "2px" }}>
                          {value || "-"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-end" style={{ marginTop: "40px", fontStyle: "italic", fontWeight: 500 }}>
                  Principal
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CertificatePreview;